import { useState } from 'react'
import { OrderTicketModal } from '@/components/orders/OrderTicketModal'
import { ProposedActionItem } from './ProposedActionItem'
import { downloadRebalanceCsv } from '@/lib/exportRebalance'
import type { ProposedAction } from '@/types/rebalance'

const PRINT_DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

interface ProposedActionsSectionProps {
  actions: ProposedAction[]
}

export function ProposedActionsSection({ actions }: ProposedActionsSectionProps) {
  const [ticketAction, setTicketAction] = useState<ProposedAction | null>(null)

  const sellCount = actions.filter((a) => a.action === 'sell').length
  const buyCount = actions.filter((a) => a.action === 'buy').length

  const positionActions = actions.filter((a) => a.source === 'position_drift')
  const clusterActions = actions.filter((a) => a.source === 'cluster_drift')

  function handleOrderTicket(action: ProposedAction) {
    setTicketAction(action)
  }

  return (
    <>
      {actions.length === 0 ? (
        <section className="hairline mb-6 border bg-panel p-4 md:p-6">
          <h2 className="tracked-caps mb-3 text-xs text-muted">Vorgeschlagene Aktionen</h2>
          <p className="font-mono text-sm text-faint">
            ✓ Kein Rebalance nötig — alle Positionen im Toleranz-Band
          </p>
        </section>
      ) : (
        <section className="hairline print-area mb-6 border bg-panel p-4 md:p-6">
          <header className="mb-3 flex flex-col items-stretch gap-1 md:flex-row md:items-baseline md:justify-between">
            <h2 className="tracked-caps text-xs text-muted">Vorgeschlagene Aktionen</h2>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] text-faint">
                {sellCount} Verkauf{sellCount === 1 ? '' : 'äufe'} · {buyCount} Kauf
                {buyCount === 1 ? '' : 'käufe'}
              </span>
              <div className="flex gap-2 print:hidden">
                <button
                  type="button"
                  onClick={() => downloadRebalanceCsv(actions)}
                  className="hairline border px-2 py-1 font-mono text-[10px] text-muted hover:text-ink"
                >
                  CSV
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="hairline border px-2 py-1 font-mono text-[10px] text-muted hover:text-ink"
                >
                  Drucken / PDF
                </button>
              </div>
            </div>
          </header>

          <p className="mb-3 hidden font-mono text-[10px] text-faint print:block">
            Rebalance-Checkliste · {PRINT_DATE_FORMATTER.format(new Date())}
          </p>

          {positionActions.length > 0 && (
            <div className="mb-3">
              <h3 className="tracked-caps mb-2 text-[11px] text-muted">Position-Drift</h3>
              <ul className="flex flex-col">
                {positionActions.map((action, index) => (
                  <ProposedActionItem
                    key={`${action.source}-${action.action}-${action.position_id}`}
                    index={index + 1}
                    action={action}
                    onOrderTicket={handleOrderTicket}
                  />
                ))}
              </ul>
            </div>
          )}

          {clusterActions.length > 0 && (
            <div>
              <h3 className="tracked-caps mb-2 text-[11px] text-muted">Cluster-Drift</h3>
              <ul className="flex flex-col">
                {clusterActions.map((action, index) => (
                  <ProposedActionItem
                    key={`${action.source}-${action.action}-${action.position_id}-${action.cluster_name}`}
                    index={index + 1}
                    action={action}
                    onOrderTicket={handleOrderTicket}
                  />
                ))}
              </ul>
            </div>
          )}

          {positionActions.length === 0 && clusterActions.length === 0 && (
            <ul className="flex flex-col">
              {actions.map((action, index) => (
                <ProposedActionItem
                  key={`${action.action}-${action.position_id}`}
                  index={index + 1}
                  action={action}
                  onOrderTicket={handleOrderTicket}
                />
              ))}
            </ul>
          )}
        </section>
      )}
      <OrderTicketModal
        open={ticketAction !== null}
        onClose={() => setTicketAction(null)}
        positionId={ticketAction?.position_id ?? null}
        positionName={ticketAction?.name}
        direction={ticketAction?.action}
        defaultOrderType="market"
        prefillEurAmount={ticketAction ? Number(ticketAction.eur_amount) : null}
      />
    </>
  )
}
